import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import type { ShopGameWallet } from '../../types';
import { GAME_WALLET_SYNC_LABELS } from '../../utils/gameWalletSync';

const SYNC_LABELS = GAME_WALLET_SYNC_LABELS;

const SYNC_COLORS: Record<ShopGameWallet['syncStatus'], string> = {
  in_sync: 'text-emerald-700',
  pending: 'text-amber-700',
  failed: 'text-red-700',
  never_synced: 'text-amber-700',
};

interface Props {
  wallet: ShopGameWallet | null;
  open: boolean;
  onClose: () => void;
}

function Field({ label, value, valueClass }: { label: string; value: React.ReactNode; valueClass?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-stone-100 py-3 text-sm last:border-0">
      <span className="shrink-0 text-stone-500">{label}</span>
      <span className={`text-right font-medium text-stone-900 ${valueClass ?? ''}`}>{value}</span>
    </div>
  );
}

export default function GameWalletDrawer({ wallet, open, onClose }: Props) {
  const { timezone } = useTimezone();
  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/25 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col border-l border-stone-200 bg-white shadow-2xl transition-transform duration-200 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Wallet detail"
      >
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-stone-900">Wallet details</h2>
            <p className="text-xs text-stone-500">Read-only view</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label="Close drawer"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {wallet ? (
            <>
              <Field label="Player" value={`${wallet.playerName} (${wallet.playerUsername})`} />
              <Field label="Player ID" value={wallet.playerId} />
              <Field label="Game wallet ID" value={wallet.gameWalletId} />
              <Field label="Temp wallet balance" value={wallet.masterCreditBalance.toFixed(2)} />
              <Field label="In-game balance" value={wallet.gameWalletBalance.toFixed(2)} />
              <Field
                label="Sync status"
                value={SYNC_LABELS[wallet.syncStatus] ?? wallet.syncStatus}
                valueClass={SYNC_COLORS[wallet.syncStatus]}
              />
              <Field
                label="Last sync"
                value={wallet.lastSyncAt ? formatInTimezone(wallet.lastSyncAt, timezone) : '—'}
              />
              {wallet.lastError && (
                <Field label="Last error" value={wallet.lastError} valueClass="text-red-700" />
              )}

              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
                Shop can view the issue reason, but cannot run Admin-level retry jobs from this page.
              </div>
            </>
          ) : (
            <p className="py-8 text-center text-sm text-stone-400">No wallet selected</p>
          )}
        </div>

        <div className="border-t border-stone-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-stone-200 bg-white py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}
